import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Table } from 'reactstrap';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import AppBadge from 'components/App/AppBadge';
import ModalImagePreview from 'components/Modals/ModalImagePreview';
import {
  COMPLAINT_STATUS,
  COMPLAINT_STATUS_LIST,
  COMPLAINT_TYPE_LIST,
  LOCATIONS,
} from 'constants/index';

import styles from './DataTableComplaintList.module.scss';

const getStatusBadgeColor = (status) => {
  switch (status) {
    case COMPLAINT_STATUS.RESOLVED:
      return 'success';
    case COMPLAINT_STATUS.PENDING:
      return 'caution';
    case COMPLAINT_STATUS.SKIPPED:
    case 'REJECTED':
      return 'error';
    default:
      return 'info';
  }
};

const getStatusLabel = (status) => {
  const found = COMPLAINT_STATUS_LIST.find((item) => item.value === status);
  return found ? found.label : status || '-';
};

const getTypeLabel = (type) => {
  const found = COMPLAINT_TYPE_LIST.find((item) => item.value === type);
  return found ? found.label : type || '-';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

function DataTableComplaintList(props) {
  const { isLoading, complaints, onOpenDetail } = props;
  const history = useHistory();
  const [preview, setPreview] = useState({
    visible: false,
    images: [],
    current: 0,
  });

  const handleOpenDetail = (complaint) => {
    if (onOpenDetail) {
      onOpenDetail(complaint);
    } else {
      history.push(`${LOCATIONS.MANAGER_COMPLAINT}/${complaint._id}`);
    }
  };

  const handlePreviewImages = (e, images, index = 0) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setPreview({
      visible: true,
      images: images || [],
      current: index,
    });
  };

  return (
    <div className={styles.Component}>
      <Loading loading={isLoading} loaderOnPage />
      <Table responsive className="Table">
        <thead className="Table__Header">
          <tr className="Table__Header__Row">
            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '180px' }}
            >
              Mã khiếu nại
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '150px' }}
            >
              Ngày gửi khiếu nại
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '200px' }}
            >
              Email
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '150px' }}
            >
              Phân loại
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '280px' }}
            >
              Nội dung
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '140px' }}
            >
              Hình ảnh
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '150px' }}
            >
              Ngày giải quyết
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '140px' }}
            >
              Trạng thái
            </th>

            <th
              className="Table__Header__Row__Content text-center"
              style={{ minWidth: '120px' }}
            >
              Thao tác
            </th>
          </tr>
        </thead>

        <tbody className="Table__Body">
          {complaints && complaints.length > 0 ? (
            complaints.map((item) => (
              <tr className="Table__Body__Row" key={item._id}>

                {/* 1. Mã khiếu nại */}
                <td className="Table__Body__Row__Content">
                  <div className="d-flex flex-column">
                    <span
                      className="Content__Id"
                      onClick={() => handleOpenDetail(item)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleOpenDetail(item);
                      }}
                    >
                      {item.complaintId || item.complainId || item.orderId || item._id}
                    </span>
                    {item.bookingCode && (
                      <span className="Content__BookingCode">
                        {`Mã: ${item.bookingCode}`}
                      </span>
                    )}
                  </div>
                </td>

                {/* 2. Ngày gửi khiếu nại */}
                <td className="Table__Body__Row__Content">
                  {formatDate(item.createdAt)}
                </td>

                {/* 3. Email */}
                <td className="Table__Body__Row__Content">
                  <div className="d-flex flex-column">
                    <span className="Content__Email">{item.email || item.user?.email}</span>
                    {(item.user?.fullName || item.customerName) && (
                      <span className="Content__Name">{item.user?.fullName || item.customerName}</span>
                    )}
                  </div>
                </td>

                {/* 4. Phân loại */}
                <td className="Table__Body__Row__Content">
                  <span className="CategoryBadge">
                    {getTypeLabel(item.type || item.category)}
                  </span>
                </td>

                {/* 5. Nội dung */}
                <td className="Table__Body__Row__Content">
                  <p className="Content__Text" title={item.content}>
                    {item.content || '-'}
                  </p>
                </td>

                {/* 6. Hình ảnh */}
                <td className="Table__Body__Row__Content">
                  {item.images && item.images.length > 0 ? (
                    <div className="ImageThumbnails">
                      {item.images.slice(0, 2).map((imgUrl, idx) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={idx}
                          className="ThumbWrapper"
                          onClick={(e) => handlePreviewImages(e, item.images, idx)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handlePreviewImages(e, item.images, idx);
                          }}
                        >
                          <img src={imgUrl} alt={`Ảnh đính kèm ${idx + 1}`} />
                        </div>
                      ))}
                      {item.images.length > 2 && (
                        <div
                          className="MoreImagesBadge"
                          onClick={(e) => handlePreviewImages(e, item.images, 2)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handlePreviewImages(e, item.images, 2);
                          }}
                        >
                          {`+${item.images.length - 2}`}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="NoImageText">Không có ảnh</span>
                  )}
                </td>

                {/* 7. Ngày giải quyết */}
                <td className="Table__Body__Row__Content">
                  {item.responseAt || item.resolvedAt
                    ? formatDate(item.responseAt || item.resolvedAt)
                    : '-'}
                </td>

                {/* 8. Trạng thái */}
                <td className="Table__Body__Row__Content">
                  <AppBadge
                    color={getStatusBadgeColor(item.status)}
                    className="Badge"
                  >
                    {getStatusLabel(item.status)}
                  </AppBadge>
                </td>

                {/* 9. Nút thao tác chi tiết */}
                <td className="Table__Body__Row__Content text-center">
                  <Button
                    outline
                    classes="Action__Button Action__Button__Link"
                    onClick={() => handleOpenDetail(item)}
                  >
                    Xem chi tiết
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr className="Table__Body__Row">
              <td
                colSpan="9"
                className="Table__Body__Row__Content text-center"
              >
                <div className="EmptyData">
                  <i className="fas fa-inbox fa-3x" />
                  <p>Không tìm thấy khiếu nại nào</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Image Preview Modal Popup */}
      <ModalImagePreview
        isOpen={preview.visible}
        images={preview.images}
        initialIndex={preview.current}
        onClose={() => setPreview((prev) => ({ ...prev, visible: false }))}
      />
    </div>
  );
}

DataTableComplaintList.propTypes = {
  isLoading: PropTypes.bool,
  complaints: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      orderId: PropTypes.string,
      bookingCode: PropTypes.string,
      createdAt: PropTypes.string,
      email: PropTypes.string,
      customerName: PropTypes.string,
      category: PropTypes.string,
      content: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.string),
      resolvedAt: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  onOpenDetail: PropTypes.func,
};

DataTableComplaintList.defaultProps = {
  isLoading: false,
  complaints: [],
  onOpenDetail: null,
};

export default memo(DataTableComplaintList);
